import { FC, useState, useEffect, useRef } from 'react';
import { getStoreState, observer, useStoreState, } from 'base/store';
import { Button } from '@base/lib/Button';
import styles from './index.module.less';
import { COOKIE_ACCESS_TOKEN, COOKIE_LOGIN_TOKEN, DEBUG_APP } from 'base/config/constant';
import { getCookie } from 'ava-util/dist/cookie';
import { Drawer } from '@base/lib/Drawer';
import { getParamInSearch } from 'ava-util/dist/request';
import { LoopRender } from '@base/snippets/LoopRender';
import cn from 'classnames';

type ILog = {
    title: string;
    data?: Record<string, any>;
}

type ILogEvent = CustomEvent<ILog>;

export function appLogActive() {
    const { envName } = getStoreState('rootStore');
    return typeof window !== 'undefined' && (envName !== 'prod' || getParamInSearch(DEBUG_APP) === '1')
}

export function addAppLogGroup(title: string, data?: Record<string, any>) {
    if (!appLogActive()) {
        return;
    }
    document.dispatchEvent(new CustomEvent('addAppLog', {
        detail: {
            title,
            data,
        }
    }));
}

const LogGroup: FC<ILog> = ({ title, data }) => {
    const [active, setActive] = useState<boolean>(false);

    return (
        <div className="app-log-group">
            <div
                className={cn('title', { active })}
                onClick={() => setActive(!active)}
            >
                {title}
            </div>
            {data && (
                <pre>{JSON.stringify(data, null, 4)}</pre>
            )}
        </div>
    );
}

const DebugContent: FC<{ logs: ILog[]; clearLogs: () => void; }> = observer(({ logs, clearLogs }) => {
    const userState = useStoreState('userStore');
    const [tokensInCookie, setTokenInCookie] = useState<Record<string, string>>();

    const getTokensInCookie = () => {
        setTokenInCookie({
            'LOGIN_TOKEN': getCookie(COOKIE_LOGIN_TOKEN) || '',
            'ACCESS_TOKEN': getCookie(COOKIE_ACCESS_TOKEN) || '',
        });
    }

    return (
        <div className={styles.container}>
            <section className="mb-4">
                <Button onClick={clearLogs}>
                    Clear Log
                </Button>

                <LoopRender
                    list={logs}
                    render={log => <LogGroup {...log}/>}
                />
            </section>

            <section className="mb-4">
                <h2 className="mb-2">userState</h2>
                <pre>{JSON.stringify(userState, null, 4)}</pre>
            </section>

            <section className="mb-4">
                <Button onClick={getTokensInCookie} className="mb-2">Get Tokens in Cookie</Button>

                {!!tokensInCookie && (
                    <ul>
                        {Object.entries(tokensInCookie).map(([key, value]) => (
                            <li key={key}>{key}: {value}</li>
                        ))}
                    </ul>
                )}
            </section>
        </div>
    );
});

const AppDebug: FC = () => {
    const [visible, setVisible] = useState(false);
    const [logs, setLogs] = useState<ILog[]>([]);
    const logsRef = useRef<ILog[]>([]);

    useEffect(() => {
        document.addEventListener('addAppLog', function(event) {
            const _logs = [
                {
                    title: (event as ILogEvent).detail.title,
                    data: (event as ILogEvent).detail.data
                },
                ...logsRef.current,
            ];
            logsRef.current = _logs;
            setLogs(_logs)
        });
    }, []);

    return (
        <>
            <Button
                type="dashed"
                shape="circle"
                size="small"
                className={styles['btn-debug']}
                onClick={() => setVisible(!visible)}
            >
                A
            </Button>

            <Drawer
                title={'App Debug'}
                open={visible}
                onClose={() => setVisible(false)}
            >
                <DebugContent
                    logs={logs}
                    clearLogs={() => {
                        logsRef.current = [];
                        setLogs([]);
                    }}
                />
            </Drawer>
        </>
    );
}

export default AppDebug;
